import { Server } from 'socket.io';
import { AuthSocket } from '../socket.bootstrap';

export function lanTransferHandler(io: Server, socket: AuthSocket) {
  const getUserRoom = (userId?: string) => {
    if (!userId) return null;
    return userId.startsWith('user:') ? userId : `user:${userId}`;
  };

  // 1. Sender offers a direct LAN transfer to receiver
  socket.on('lan:transfer_offer', (data: {
    transferId: string;
    toUserId?: string;
    roomId?: string; // conversationId or channelId
    fileInfo: {
      name: string;
      size: number;
      mimeType: string;
      sha256?: string;
    };
    serverInfo: {
      ips: string[];
      port: number;
      token: string;
    };
  }) => {
    const perms = socket.permissions || [];
    const hasPerm = perms.includes('*') || perms.includes('chat_lan_file_transfer') || perms.includes('team_lan_file_transfer') || perms.includes('chat_file_sharing');
    if (!hasPerm) return;

    const payload = {
      ...data,
      fromUserId: socket.userId,
      fromDisplayName: socket.displayName || socket.username,
      timestamp: Date.now(),
    };

    if (data.toUserId) {
      const uRoom = getUserRoom(data.toUserId);
      if (uRoom) io.to(uRoom).emit('lan:transfer_offer', payload);
      // Trigger instant attention buzzer notification to receiver
      const convId = data.roomId ? data.roomId.replace('conversation:', '') : undefined;
      if (convId && uRoom) {
        io.to(uRoom).emit('attention:received', {
          conversationId: convId,
          fromUserId: socket.userId,
          fromDisplayName: socket.displayName || socket.username,
          emoji: '⚡',
        });
      }
    }
    if (data.roomId) {
      socket.to(data.roomId).emit('lan:transfer_offer', payload);
    }
  });

  // 2. Receiver accepts the direct LAN transfer offer
  socket.on('lan:transfer_accept', (data: {
    transferId: string;
    toUserId?: string;
    roomId?: string;
    selectedIp?: string;
    port?: number;
  }) => {
    const payload = {
      transferId: data.transferId,
      acceptedByUserId: socket.userId,
      acceptedByName: socket.displayName || socket.username,
      selectedIp: data.selectedIp,
      port: data.port,
    };

    if (data.toUserId) {
      const uRoom = getUserRoom(data.toUserId);
      if (uRoom) io.to(uRoom).emit('lan:transfer_accept', payload);
    }
    if (data.roomId) {
      socket.to(data.roomId).emit('lan:transfer_accept', payload);
    }
  });

  // 3. Receiver rejects the offer
  socket.on('lan:transfer_reject', (data: {
    transferId: string;
    toUserId?: string;
    roomId?: string;
    reason?: string;
  }) => {
    const payload = {
      transferId: data.transferId,
      rejectedByUserId: socket.userId,
      reason: data.reason || 'Declined by user',
    };

    if (data.toUserId) {
      const uRoom = getUserRoom(data.toUserId);
      if (uRoom) io.to(uRoom).emit('lan:transfer_reject', payload);
    }
    if (data.roomId) {
      socket.to(data.roomId).emit('lan:transfer_reject', payload);
    }
  });

  // 4. Either side cancels or aborts the transfer
  socket.on('lan:transfer_cancel', (data: {
    transferId: string;
    toUserId?: string;
    roomId?: string;
    reason?: string;
  }) => {
    const payload = {
      transferId: data.transferId,
      cancelledByUserId: socket.userId,
      reason: data.reason || 'Transfer cancelled',
    };

    if (data.toUserId) {
      const uRoom = getUserRoom(data.toUserId);
      if (uRoom) io.to(uRoom).emit('lan:transfer_cancel', payload);
    }
    if (data.roomId) {
      socket.to(data.roomId).emit('lan:transfer_cancel', payload);
    }
  });

  // 5. Progress sync between peers (optional peer indicator)
  socket.on('lan:transfer_progress', (data: {
    transferId: string;
    toUserId?: string;
    roomId?: string;
    progress: number;
    speedMBs: number;
    transferredBytes: number;
    totalBytes: number;
  }) => {
    if (data.toUserId) {
      const uRoom = getUserRoom(data.toUserId);
      if (uRoom) io.to(uRoom).emit('lan:transfer_progress', data);
    }
    if (data.roomId) {
      socket.to(data.roomId).emit('lan:transfer_progress', data);
    }
  });

  // 6. Transfer completed & verified
  socket.on('lan:transfer_complete', (data: {
    transferId: string;
    toUserId?: string;
    roomId?: string;
    fileName?: string;
    size?: number;
    sha256?: string;
  }) => {
    const payload = {
      ...data,
      completedByUserId: socket.userId,
      timestamp: Date.now(),
    };

    if (data.toUserId) {
      const uRoom = getUserRoom(data.toUserId);
      if (uRoom) io.to(uRoom).emit('lan:transfer_complete', payload);
    }
    if (data.roomId) {
      socket.to(data.roomId).emit('lan:transfer_complete', payload);
    }
  });
}
